/**
 * IMBEE-9629 — recognising the channel's ending prompt in the incoming message stream.
 *
 * The ending prompt is a BOT, not a ticket hook. `Bot::enableWebChatEndingPromptBot` in core-php
 * watches for an agent message whose text equals the channel's configured ending message, and
 * replies with Yes/No buttons. The widget therefore has to notice that same agent message to
 * know the buttons that follow are the ending prompt rather than an ordinary interactive reply.
 *
 * This restores the comparison from the block that was commented out in App.tsx, which is why
 * `enableSurvey` and the whole rating flow have never done anything.
 */

/**
 * Does this incoming agent message match the channel's configured ending message?
 *
 * Three conditions, because core-php's bot has three and the widget must not arm on fewer
 * (review finding F1). `Bot::enableWebChatEndingPromptBot` only exists when the channel's
 * `enableEndingPrompt` config is on — `ApiKeyWebChatCommonForm::ensureWebChatEndingPromptBot`
 * deletes the bot otherwise — so with the toggle off no Yes/No buttons will ever arrive. A
 * widget that armed on the text alone would leave `isEndingMessage` set for the life of the
 * page, and the next interactive button from any source would be routed into the ending branch:
 * a `no` label there drops the message history, ends the session and mints a new visitor token.
 *
 * Case-insensitive but NOT trimmed (review finding F3). `BotScenario::conditionTextEquals`
 * compares `strtolower($messageModel->text) === strtolower($text)` with no trim, so trimming
 * here made the widget looser than the bot: an agent typing the ending sentence with a trailing
 * space armed the widget while the bot stayed silent — the same broken state as above, from a
 * character nobody can see.
 *
 * Both texts must be non-empty: a channel with no ending message configured must never match,
 * or every empty agent message would arm the prompt.
 */
export const isEndingPromptMessage = (
    incomingText: string | undefined | null,
    configuredEndingMessage: string | undefined | null,
    enableEndingPrompt: boolean | undefined,
): boolean => {
    if (enableEndingPrompt !== true) return false;
    const incoming = (incomingText ?? '').toLowerCase();
    const configured = (configuredEndingMessage ?? '').toLowerCase();
    if (!incoming || !configured) return false;
    return incoming === configured;
};

/**
 * Which pane follows the visitor ending the chat.
 *
 * `enableSurvey` has never been read by the widget, so a channel with the rating switched on
 * went straight to the "Chat Ended" confirmation. With it off, that confirmation stays the
 * correct destination — the survey is opt-in.
 */
export const shouldShowRatingSurvey = (enableSurvey: boolean | undefined): boolean =>
    enableSurvey === true;
